import React, { useState } from 'react';
import {
    Table, Button, Space, Typography, Tag, message, Modal, Form,
    Input, InputNumber, Select, Drawer, Card, Breadcrumb, Alert, Tooltip,
    Row, Col, Tree, Radio, Checkbox, Pagination
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleFilled,
    SearchOutlined, ReloadOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// ══════════════════════════════════════════════
//  常量与 Mock 数据
// ══════════════════════════════════════════════

// 转化服务类型（可扩展，事件云存为首个玩法）
// TODO: 联调时替换为接口数据
const SERVICE_TYPE_MAP = {
    event_cloud: { label: '事件云存储', color: 'blue' },
};
const SERVICE_TYPES = [
    { value: 'event_cloud', label: '事件云存储' },
];

// 转化服务类型 → 关联算法（复用 package/template 服务类型-算法映射）
const MOCK_SERVICES = [
    { id: 'event_cloud', serviceName: '事件云存储', serviceId: '300011', aiiotTypeConfig: '[{"AlgorithmType":1010,"AIIoTName":"云端录像","Config":{}},{"AlgorithmType":1013,"AIIoTName":"事件记录","Config":{}},{"AlgorithmType":1011,"AIIoTName":"云端抓图","Config":{}}]' },
];

// 各算法类型默认配置
const DEFAULT_CONFIGS = {
    1010: { StorageAbility: 0, StorageList: [{ LoopDays: '7', RecordType: '1' }], StreamID: 0, Duration: 30 },
    1011: { GifFlag: 0, PicType: 1, LoopDays: 7, RecordType: 1, SnapJpgCnt: 1, StorageType: 0, Interval: 30, OpenFlag: 1 },
    1013: { LoopDays: 7 },
};

// 适用 App 树：一级 App，二级 系统平台-区域
const MOCK_APP_TREE = [
    {
        key: 'app_001', title: '牵心',
        children: [
            { key: 'app_001__ios_mainland', title: 'iOS-国内' },
            { key: 'app_001__android_mainland', title: 'Android-国内' },
        ],
    },
    {
        key: 'app_002', title: '牵心Pro',
        children: [
            { key: 'app_002__ios_mainland', title: 'iOS-国内' },
            { key: 'app_002__android_mainland', title: 'Android-国内' },
            { key: 'app_002__ios_overseas', title: 'iOS-海外' },
        ],
    },
    {
        key: 'app_003', title: '鹤梦之家',
        children: [
            { key: 'app_003__ios_mainland', title: 'iOS-国内' },
            { key: 'app_003__android_mainland', title: 'Android-国内' },
        ],
    },
    {
        key: 'app_004', title: 'HomeStar',
        children: [
            { key: 'app_004__android_global', title: 'Android-全球' },
            { key: 'app_004__ios_overseas', title: 'iOS-海外' },
        ],
    },
    {
        key: 'app_005', title: '云视通',
        children: [
            { key: 'app_005__ios_overseas', title: 'iOS-海外' },
            { key: 'app_005__android_overseas', title: 'Android-海外' },
        ],
    },
];

const getAppTitle = (key) => {
    const find = (nodes) => {
        for (let i = 0; i < nodes.length; i += 1) {
            if (nodes[i].key === key) return nodes[i].title;
            if (nodes[i].children) {
                const t = find(nodes[i].children);
                if (t) return t;
            }
        }
        return null;
    };
    return find(MOCK_APP_TREE) || key;
};

// 生成适用 App 树形文本（tooltip 用）
const buildAppTreeText = (appKeys) => {
    const lines = [];
    MOCK_APP_TREE.forEach((app) => {
        if (!(appKeys || []).includes(app.key)) return;
        lines.push(app.title);
        (app.children || []).forEach((c) => {
            if ((appKeys || []).includes(c.key)) lines.push(`  ${c.title}`);
        });
    });
    return lines.join('\n');
};

// 企业 / 产品（添加产品级联）
const MOCK_ENTERPRISES = [
    { entId: 'ENT_001', entName: '深圳智联科技有限公司' },
    { entId: 'ENT_002', entName: '北京安视智能科技' },
    { entId: 'ENT_003', entName: '上海物联网科技集团' },
];

const MOCK_PRODUCTS_BY_ENT = {
    'ENT_001': [
        { pid: 'PID_100234', pname: 'IPC Pro 标准版' },
        { pid: 'PID_200891', pname: 'IPC Lite 简易版' },
        { pid: 'PID_100567', pname: 'IPC 全景摄像机' },
        { pid: 'PID_100892', pname: 'IPC 变焦枪机' },
        { pid: 'PID_200445', pname: '半球网络摄像机' },
        { pid: 'PID_200778', pname: '户外防水摄像机' },
        { pid: 'PID_100913', pname: '4K超清摄像机' },
        { pid: 'PID_200334', pname: '红外夜视摄像机' },
        { pid: 'PID_100781', pname: '无线电池摄像机' },
        { pid: 'PID_200556', pname: '云台小球机' },
        { pid: 'PID_100669', pname: '双光警戒摄像机' },
        { pid: 'PID_200223', pname: '星光级摄像机' },
        { pid: 'PID_100447', pname: '智能追踪摄像机' },
        { pid: 'PID_200901', pname: '低功耗摄像机' },
        { pid: 'PID_100112', pname: '车载摄像机' },
    ],
    'ENT_002': [
        { pid: 'PID_305672', pname: '智能门锁标准版' },
        { pid: 'PID_412038', pname: '智能插座Pro' },
        { pid: 'PID_306891', pname: '智能门铃' },
        { pid: 'PID_418234', pname: '智能灯泡' },
        { pid: 'PID_307445', pname: '智能窗帘电机' },
        { pid: 'PID_419902', pname: '智能温湿度传感器' },
        { pid: 'PID_308776', pname: '智能烟雾报警器' },
        { pid: 'PID_420113', pname: '智能水浸传感器' },
        { pid: 'PID_309018', pname: '智能网关' },
        { pid: 'PID_421556', pname: '智能猫眼门锁' },
        { pid: 'PID_310445', pname: '智能中控屏' },
        { pid: 'PID_422887', pname: '智能人体传感器' },
        { pid: 'PID_311234', pname: '智能空调伴侣' },
        { pid: 'PID_423661', pname: '智能红外遥控器' },
        { pid: 'PID_312890', pname: '智能开关面板' },
    ],
    'ENT_003': [
        { pid: 'PID_523149', pname: '云台摄像机旗舰版' },
        { pid: 'PID_524871', pname: '云台摄像机标准版' },
        { pid: 'PID_525903', pname: '高速球机' },
        { pid: 'PID_526452', pname: '激光球机' },
        { pid: 'PID_527118', pname: '红外球机' },
        { pid: 'PID_528774', pname: '全景球机' },
        { pid: 'PID_529330', pname: '智能跟踪球机' },
        { pid: 'PID_530662', pname: '低照度球机' },
        { pid: 'PID_531887', pname: '枪球联动摄像机' },
        { pid: 'PID_532440', pname: '半球云台机' },
        { pid: 'PID_533995', pname: '4G球机' },
        { pid: 'PID_534551', pname: '太阳能球机' },
        { pid: 'PID_535108', pname: '变倍球机' },
        { pid: 'PID_536674', pname: '防爆球机' },
        { pid: 'PID_537230', pname: '热成像球机' },
    ],
};

// 策略列表（适用 App 为策略级，App → 系统平台-区域 树形）
const MOCK_STRATEGIES = [
    {
        key: '1', name: 'test-事件云存试用转化', strategyId: 'test-tc-001',
        serviceType: 'event_cloud', freePlaySeconds: 30,
        unlockPriceCNY: 9.9, unlockPriceUSD: 1.49,
        appKeys: ['app_001', 'app_001__ios_mainland', 'app_001__android_mainland'],
        // 跨 3 家企业绑定 13 个产品，用于演示列表分页 / 企业分组排序 / 搜索过滤 / 批量移除
        products: [
            { pid: 'PID_100234', pname: 'IPC Pro 标准版', entId: 'ENT_001', entName: '深圳智联科技有限公司' },
            { pid: 'PID_200891', pname: 'IPC Lite 简易版', entId: 'ENT_001', entName: '深圳智联科技有限公司' },
            { pid: 'PID_100567', pname: 'IPC 全景摄像机', entId: 'ENT_001', entName: '深圳智联科技有限公司' },
            { pid: 'PID_100892', pname: 'IPC 变焦枪机', entId: 'ENT_001', entName: '深圳智联科技有限公司' },
            { pid: 'PID_200445', pname: '半球网络摄像机', entId: 'ENT_001', entName: '深圳智联科技有限公司' },
            { pid: 'PID_305672', pname: '智能门锁标准版', entId: 'ENT_002', entName: '北京安视智能科技' },
            { pid: 'PID_412038', pname: '智能插座Pro', entId: 'ENT_002', entName: '北京安视智能科技' },
            { pid: 'PID_306891', pname: '智能门铃', entId: 'ENT_002', entName: '北京安视智能科技' },
            { pid: 'PID_418234', pname: '智能灯泡', entId: 'ENT_002', entName: '北京安视智能科技' },
            { pid: 'PID_523149', pname: '云台摄像机旗舰版', entId: 'ENT_003', entName: '上海物联网科技集团' },
            { pid: 'PID_524871', pname: '云台摄像机标准版', entId: 'ENT_003', entName: '上海物联网科技集团' },
            { pid: 'PID_525903', pname: '高速球机', entId: 'ENT_003', entName: '上海物联网科技集团' },
            { pid: 'PID_527118', pname: '红外球机', entId: 'ENT_003', entName: '上海物联网科技集团' },
        ],
        enabled: true,
    },
    {
        key: '2', name: 'test-牵心Pro云存解锁', strategyId: 'test-tc-002',
        serviceType: 'event_cloud', freePlaySeconds: 30,
        unlockPriceCNY: 6.6, unlockPriceUSD: 0.99,
        appKeys: ['app_002', 'app_002__ios_mainland', 'app_002__android_mainland', 'app_002__ios_overseas'],
        products: [
            { pid: 'PID_200891', pname: 'IPC Lite 简易版', entId: 'ENT_001', entName: '深圳智联科技有限公司' },
        ],
        enabled: true,
    },
    {
        key: '3', name: 'test-海外版事件解锁', strategyId: 'test-tc-003',
        serviceType: 'event_cloud', freePlaySeconds: 30,
        unlockPriceCNY: 12.0, unlockPriceUSD: 1.99,
        appKeys: ['app_004', 'app_004__ios_overseas', 'app_004__android_global', 'app_005', 'app_005__ios_overseas'],
        products: [
            { pid: 'PID_523149', pname: '云台摄像机旗舰版', entId: 'ENT_003', entName: '上海物联网科技集团' },
        ],
        enabled: false,
    },
    {
        key: '4', name: 'test-智能门锁试用', strategyId: 'test-tc-004',
        serviceType: 'event_cloud', freePlaySeconds: 30,
        unlockPriceCNY: 3.9, unlockPriceUSD: 0.59,
        appKeys: ['app_003', 'app_003__ios_mainland', 'app_003__android_mainland'],
        products: [
            { pid: 'PID_305672', pname: '智能门锁标准版', entId: 'ENT_002', entName: '北京安视智能科技' },
            { pid: 'PID_412038', pname: '智能插座Pro', entId: 'ENT_002', entName: '北京安视智能科技' },
        ],
        enabled: true,
    },
    {
        key: '5', name: 'test-已停用转化策略', strategyId: 'test-tc-005',
        serviceType: 'event_cloud', freePlaySeconds: 30,
        unlockPriceCNY: 8.8, unlockPriceUSD: 1.29,
        appKeys: ['app_005', 'app_005__android_overseas'],
        products: [
            { pid: 'PID_100234', pname: 'IPC Pro 标准版', entId: 'ENT_001', entName: '深圳智联科技有限公司' },
        ],
        enabled: false,
    },
];

// ══════════════════════════════════════════════
//  子组件：添加产品弹窗（企业 → 产品多选）
// ══════════════════════════════════════════════
const PRODUCT_PAGE_SIZE = 6;                                            // 产品平铺列表每页条数

const AddProductModal = ({ open, existingPids, onCancel, onSubmit }) => {
    const [entSearchType, setEntSearchType] = useState('name');          // 企业查询方式：name=名称模糊 / id=ID精准
    const [entIdInput, setEntIdInput] = useState('');                    // 企业ID精准查询输入
    const [selectedEnt, setSelectedEnt] = useState(null);                // { entId, entName }
    const [selectedPids, setSelectedPids] = useState([]);                // 已勾选产品 pid 列表
    const [productPage, setProductPage] = useState(1);                   // 产品分页页码

    // 待选择产品：排除已在绑定列表中的产品
    const products = selectedEnt
        ? (MOCK_PRODUCTS_BY_ENT[selectedEnt.entId] || []).filter((p) => !existingPids.includes(p.pid))
        : [];

    // 当前页展示的产品切片
    const pagedProducts = products.slice((productPage - 1) * PRODUCT_PAGE_SIZE, productPage * PRODUCT_PAGE_SIZE);

    React.useEffect(() => {
        if (open) {
            setEntSearchType('name');
            setEntIdInput('');
            setSelectedEnt(null);
            setSelectedPids([]);
            setProductPage(1);
        }
    }, [open]);

    const resetProducts = () => { setSelectedPids([]); setProductPage(1); };

    const handleSelectEnt = (entId) => {
        const ent = MOCK_ENTERPRISES.find((e) => e.entId === entId);
        setSelectedEnt(ent ? { entId: ent.entId, entName: ent.entName } : null);
        resetProducts();
    };

    const handleIdQuery = () => {
        const kw = entIdInput.trim();
        if (!kw) { message.warning('请输入企业ID'); return; }
        const ent = MOCK_ENTERPRISES.find((e) => e.entId === kw);
        if (!ent) { setSelectedEnt(null); resetProducts(); message.warning('未查询到该企业ID对应的企业'); return; }
        setSelectedEnt({ entId: ent.entId, entName: ent.entName });
        resetProducts();
    };

    const handleSubmit = () => {
        if (!selectedEnt) { message.warning('请选择企业'); return; }
        if (selectedPids.length === 0) { message.warning('请选择产品'); return; }
        const newProds = products
            .filter((p) => selectedPids.includes(p.pid))
            .map((p) => ({ pid: p.pid, pname: p.pname, entId: selectedEnt.entId, entName: selectedEnt.entName }));
        onSubmit(newProds);
    };

    return (
        <Modal
            title="添加产品"
            open={open}
            width={520}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>取消</Button>,
                <Button key="save" type="primary" onClick={handleSubmit}>确认添加</Button>,
            ]}
        >
            {/* ① 选择企业：名称模糊下拉 / ID精准查询 可切换 */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 6 }}>
                    <Text><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>选择企业</Text>
                </div>
                <Space.Compact style={{ width: '100%' }}>
                    <Select
                        value={entSearchType}
                        onChange={(v) => { setEntSearchType(v); setSelectedEnt(null); setEntIdInput(''); resetProducts(); }}
                        style={{ width: 110 }}
                        options={[{ value: 'name', label: '企业名称' }, { value: 'id', label: '企业ID' }]}
                    />
                    {entSearchType === 'name' ? (
                        <Select
                            style={{ width: '100%' }}
                            placeholder="请输入企业名称模糊查询"
                            showSearch
                            allowClear
                            value={selectedEnt?.entId}
                            onChange={handleSelectEnt}
                            optionFilterProp="label"
                            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                            options={MOCK_ENTERPRISES.map((e) => ({ value: e.entId, label: `${e.entName}（${e.entId}）` }))}
                        />
                    ) : (
                        <>
                            <Input
                                style={{ width: '100%' }}
                                placeholder="请输入企业ID精准查询"
                                value={entIdInput}
                                onChange={(e) => setEntIdInput(e.target.value)}
                                onPressEnter={handleIdQuery}
                                allowClear
                            />
                            <Button type="primary" onClick={handleIdQuery}>查询</Button>
                        </>
                    )}
                </Space.Compact>
                {selectedEnt && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#f6f8ff', borderRadius: 6, border: '1px solid #d6e4ff' }}>
                        <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>已选企业</Text>
                        <Text strong>{selectedEnt.entName}</Text>
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{selectedEnt.entId}</Text>
                    </div>
                )}
            </div>

            {/* ② 选择产品：复选框平铺列表（名称 + ID），排除已添加产品 */}
            <div>
                <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>选择产品</Text>
                    {selectedEnt && products.length > 0 && (
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setSelectedPids([])}>清空</Button>
                    )}
                </div>
                {!selectedEnt ? (
                    <Alert type="info" showIcon message="请先选择企业" style={{ borderRadius: 6 }} />
                ) : products.length === 0 ? (
                    <Alert type="info" showIcon message="该企业下产品均已添加或暂无可选产品" style={{ borderRadius: 6 }} />
                ) : (
                    <>
                        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '4px 8px', minHeight: 232 }}>
                            <Checkbox.Group
                                value={selectedPids}
                                onChange={(vals) => setSelectedPids(vals)}
                                style={{ width: '100%', display: 'block' }}
                            >
                                {pagedProducts.map((p) => (
                                    <div key={p.pid} style={{ padding: '8px 4px', borderBottom: '1px solid #f5f5f5' }}>
                                        <Checkbox value={p.pid}>
                                            <span style={{ fontWeight: 500 }}>{p.pname}</span>
                                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{p.pid}</Text>
                                        </Checkbox>
                                    </div>
                                ))}
                            </Checkbox.Group>
                        </div>
                        {products.length > PRODUCT_PAGE_SIZE && (
                            <Pagination
                                style={{ marginTop: 12, textAlign: 'right' }}
                                size="small"
                                current={productPage}
                                pageSize={PRODUCT_PAGE_SIZE}
                                total={products.length}
                                showSizeChanger={false}
                                showTotal={(t) => `共 ${t} 个产品`}
                                onChange={setProductPage}
                            />
                        )}
                        {existingPids.length > 0 && (
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>已在绑定列表中的产品已自动排除</Text>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

// ══════════════════════════════════════════════
//  子组件：策略编辑抽屉
// ══════════════════════════════════════════════
const StrategyFormDrawer = ({ open, editingRecord, occupiedPlatformKeys = [], onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [products, setProducts] = useState([]);
    const [addProductOpen, setAddProductOpen] = useState(false);
    const [appKeys, setAppKeys] = useState([]);
    const [algorithmConfigs, setAlgorithmConfigs] = useState({});
    const [productFilterType, setProductFilterType] = useState('productName');  // 绑定产品搜索类型
    const [productKeyword, setProductKeyword] = useState('');                  // 绑定产品搜索关键字
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);                // 批量移除选中行

    const serviceType = Form.useWatch('serviceType', form);

    // 绑定产品：搜索过滤 + 按企业分组排序（同企业相邻）
    const filteredSortedProducts = products
        .filter((p) => {
            const kw = productKeyword.trim().toLowerCase();
            if (!kw) return true;
            return productFilterType === 'productName'
                ? p.pname.toLowerCase().includes(kw)
                : p.pid.toLowerCase().includes(kw);
        })
        .sort((a, b) => {
            if (a.entId === b.entId) return 0;
            return a.entId < b.entId ? -1 : 1;
        });

    // 解析转化服务类型关联的算法
    const parseAlgorithms = (sid) => {
        if (!sid) return [];
        const st = MOCK_SERVICES.find((s) => s.id === sid);
        if (!st) return [];
        try { return JSON.parse(st.aiiotTypeConfig || '[]'); } catch { return []; }
    };

    const initAlgorithmConfigs = (sid) => {
        const algos = parseAlgorithms(sid);
        const configs = {};
        algos.forEach((a) => {
            configs[a.AlgorithmType] = { ...(DEFAULT_CONFIGS[a.AlgorithmType] || {}) };
        });
        setAlgorithmConfigs(configs);
    };

    const setAlgoConfig = (type, patch) => {
        setAlgorithmConfigs((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));
    };

    // 渲染各算法配置项
    const renderAlgoConfig = (type) => {
        const cfg = algorithmConfigs[type] || {};
        const setCfg = (patch) => setAlgoConfig(type, patch);

        if (type === 1010) {
            const list = cfg.StorageList || [];
            const setItem = (i, key, val) => {
                const l = [...list];
                l[i] = { ...l[i], [key]: val };
                setCfg({ StorageList: l });
            };
            return (
                <div>
                    <Form.Item label="录制码流" style={{ marginBottom: 16 }}>
                        <Select value={cfg.StreamID} onChange={(v) => setCfg({ StreamID: v })} style={{ width: 220 }}>
                            <Select.Option value={0}>主码流(码流ID: 0)</Select.Option>
                            <Select.Option value={1}>子码流(码流ID: 1)</Select.Option>
                            <Select.Option value={2}>次码流(码流ID: 2)</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="默认报警录制最大时长(秒)" style={{ marginBottom: 16 }}>
                        <InputNumber min={0} value={cfg.Duration} onChange={(v) => setCfg({ Duration: v })} style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item label="存储列表" style={{ marginBottom: 0 }}>
                        <div style={{ width: '100%', border: '1px solid #e4e7ed', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', padding: '0 12px', gap: 8, background: '#f5f7fa', height: 36, fontSize: 13, color: '#606266', fontWeight: 500, borderBottom: '1px solid #e4e7ed', alignItems: 'center' }}>
                                <span style={{ flex: 1 }}>存储天数</span>
                                <span style={{ flex: 1 }}>存储方式</span>
                            </div>
                            {list.map((item, i) => (
                                <div key={i} style={{ display: 'flex', padding: '0 12px', gap: 8, height: 52, borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
                                    <span style={{ flex: 1 }}>
                                        <InputNumber min={0} value={Number(item.LoopDays)} onChange={(v) => setItem(i, 'LoopDays', String(v))} style={{ width: '100%' }} />
                                    </span>
                                    <span style={{ flex: 1 }}>
                                        <Select value={item.RecordType} onChange={(v) => setItem(i, 'RecordType', v)} style={{ width: '100%' }}>
                                            <Select.Option value="1">全天</Select.Option>
                                            <Select.Option value="2">事件</Select.Option>
                                        </Select>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Form.Item>
                </div>
            );
        }

        if (type === 1013) {
            return (
                <Form.Item label="存储天数" style={{ marginBottom: 0 }}>
                    <InputNumber min={1} value={cfg.LoopDays} onChange={(v) => setCfg({ LoopDays: v })} style={{ width: 220 }} /> <span style={{ color: '#8c8c8c', marginLeft: 8 }}>天</span>
                </Form.Item>
            );
        }

        if (type === 1011) {
            return (
                <div>
                    <Form.Item label="保存天数" style={{ marginBottom: 16 }}>
                        <InputNumber min={1} value={cfg.LoopDays} onChange={(v) => setCfg({ LoopDays: v })} style={{ width: 220 }} /> <span style={{ color: '#8c8c8c', marginLeft: 8 }}>天</span>
                    </Form.Item>
                    <Form.Item label="抓图质量" style={{ marginBottom: 16 }}>
                        <Select value={cfg.PicType} onChange={(v) => setCfg({ PicType: v })} style={{ width: 220 }}>
                            <Select.Option value={1}>大图</Select.Option>
                            <Select.Option value={2}>小图</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="默认开启" style={{ marginBottom: 0 }}>
                        <Radio.Group value={cfg.OpenFlag} onChange={(e) => setCfg({ OpenFlag: e.target.value })}>
                            <Radio value={1}>开启</Radio>
                            <Radio value={0}>关闭</Radio>
                        </Radio.Group>
                    </Form.Item>
                </div>
            );
        }

        return <Alert type="info" showIcon message="暂无配置项" />;
    };

    // 已选服务内容的配置（选中服务类型后展示）
    const renderAlgoConfigSection = () => {
        const algos = parseAlgorithms(serviceType);
        if (!serviceType || algos.length === 0) return null;
        return (
            <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ width: 4, height: 16, background: '#1890ff', borderRadius: 2 }} />
                    <Text strong>已选服务内容的配置</Text>
                </div>
                <Alert type="info" showIcon message="以下算法由所选服务类型自动关联，仅支持修改参数值。" style={{ marginBottom: 12 }} />
                {algos.map((algo) => {
                    const type = algo.AlgorithmType;
                    return (
                        <div key={type} style={{ border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 10, overflow: 'hidden', background: '#fff' }}>
                            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                <Tag color="blue" style={{ border: 'none', marginRight: 8 }}>{type}</Tag>
                                <Text strong>{algo.AIIoTName}</Text>
                            </div>
                            <div style={{ padding: 16 }}>{renderAlgoConfig(type)}</div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // 适用 App 树：已被其他策略占用的「系统平台-区域」置灰不可选；App 下全部占用时 App 一并置灰
    const appTreeData = MOCK_APP_TREE.map((app) => {
        const children = (app.children || []).map((c) => ({
            ...c,
            disabled: occupiedPlatformKeys.includes(c.key),
        }));
        const allDisabled = children.length > 0 && children.every((c) => c.disabled);
        return { ...app, disabled: allDisabled, children };
    });

    // 全选：仅选中未被占用的节点（跳过已置灰的平台-区域）
    const handleCheckAll = () => {
        const keys = [];
        MOCK_APP_TREE.forEach((app) => {
            const available = (app.children || []).filter((c) => !occupiedPlatformKeys.includes(c.key));
            if (available.length > 0) {
                keys.push(app.key);
                available.forEach((c) => keys.push(c.key));
            }
        });
        setAppKeys(keys);
    };

    React.useEffect(() => {
        if (open) {
            if (editingRecord) {
                form.setFieldsValue({
                    name: editingRecord.name,
                    serviceType: editingRecord.serviceType,
                    freePlaySeconds: editingRecord.freePlaySeconds,
                    unlockPriceCNY: editingRecord.unlockPriceCNY,
                    unlockPriceUSD: editingRecord.unlockPriceUSD,
                });
                setProducts(editingRecord.products.map((p) => ({ ...p })));
                setAppKeys(editingRecord.appKeys || []);
                initAlgorithmConfigs(editingRecord.serviceType);
            } else {
                form.resetFields();
                form.setFieldsValue({ serviceType: 'event_cloud' });
                setProducts([]);
                setAppKeys([]);
                initAlgorithmConfigs('event_cloud');
            }
            setProductFilterType('productName');
            setProductKeyword('');
            setSelectedRowKeys([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingRecord, form]);

    // 切换转化服务类型时重新初始化算法配置
    React.useEffect(() => {
        if (open && serviceType) initAlgorithmConfigs(serviceType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceType, open]);

    const handleAddProduct = (prods) => {
        setProducts((prev) => [...prev, ...prods]);
        setAddProductOpen(false);
        message.success(`已添加 ${prods.length} 个产品`);
    };

    const handleRemoveProduct = (pid) => {
        setProducts((prev) => prev.filter((p) => p.pid !== pid));
        setSelectedRowKeys((prev) => prev.filter((k) => k !== pid));
    };

    const handleBatchRemove = () => {
        if (selectedRowKeys.length === 0) return;
        setProducts((prev) => prev.filter((p) => !selectedRowKeys.includes(p.pid)));
        message.success(`已移除 ${selectedRowKeys.length} 个产品`);
        setSelectedRowKeys([]);
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const values = await form.validateFields();
            const platformKeys = appKeys.filter((k) => k.includes('__'));
            if (platformKeys.length === 0) {
                message.error('请为适用 App 至少选择一个系统平台-区域'); setSubmitting(false); return;
            }
            if (products.length === 0) {
                message.error('请至少添加一个绑定产品'); setSubmitting(false); return;
            }
            const algos = parseAlgorithms(values.serviceType);
            const serviceConfig = algos.map((algo) => ({
                AlgorithmType: algo.AlgorithmType,
                AIIoTName: algo.AIIoTName,
                Config: algorithmConfigs[algo.AlgorithmType] || {},
            }));
            onSubmit({
                ...values,
                appKeys,
                products,
                serviceConfig: JSON.stringify(serviceConfig),
                enabled: editingRecord ? editingRecord.enabled : true,
            });
            setSubmitting(false);
        } catch {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Drawer
                title={editingRecord ? '编辑增值转化运营策略' : '添加增值转化运营策略'}
                open={open}
                width={800}
                onClose={onCancel}
                footer={
                    <Space style={{ float: 'right' }}>
                        <Button onClick={onCancel}>取消</Button>
                        <Button type="primary" loading={submitting} onClick={handleSubmit}>保存策略</Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="name" label="策略名称" rules={[{ required: true, message: '请输入策略名称' }]}>
                                <Input placeholder="请输入策略名称" maxLength={30} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="serviceType" label="转化服务类型" rules={[{ required: true, message: '请选择转化服务类型' }]}>
                                <Select placeholder="请选择转化服务类型" options={SERVICE_TYPES} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {renderAlgoConfigSection()}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="freePlaySeconds" label="免费回看时长（秒）" rules={[{ required: true, message: '请输入免费回看时长' }]}>
                                <InputNumber min={1} style={{ width: '100%' }} placeholder="如 30" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="unlockPriceCNY" label="单次解锁定价（国内）" rules={[{ required: true, message: '请输入国内解锁价' }]}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="¥" placeholder="如 9.90" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="unlockPriceUSD" label="单次解锁定价（海外）" rules={[{ required: true, message: '请输入海外解锁价' }]}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" placeholder="如 1.49" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 适用 App：App → 系统平台-区域 两级复选 */}
                    <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text strong>适用 App</Text>
                            <Space size={4}>
                                <Button type="link" size="small" style={{ padding: 0 }} onClick={handleCheckAll}>全选</Button>
                                <Text type="secondary">/</Text>
                                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setAppKeys([])}>清空</Button>
                            </Space>
                        </div>
                        <Tree
                            checkable
                            selectable={false}
                            treeData={appTreeData}
                            checkedKeys={appKeys}
                            onCheck={(keys) => setAppKeys(keys)}
                        />
                        <Alert
                            type="info" showIcon style={{ marginTop: 8, borderRadius: 6 }} banner
                            message="策略级配置，该策略下所有绑定产品均适用于以下 App 及系统平台-区域"
                        />
                    </div>

                    <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                            <Text strong>绑定产品{products.length > 0 && <Text type="secondary" style={{ fontWeight: 400 }}>（已绑定 {products.length} 个）</Text>}</Text>
                            <Space>
                                {selectedRowKeys.length > 0 && (
                                    <Button type="link" danger size="small" onClick={handleBatchRemove}>批量移除（{selectedRowKeys.length}）</Button>
                                )}
                                <Button type="primary" ghost icon={<PlusOutlined />} size="small" onClick={() => setAddProductOpen(true)}>添加产品</Button>
                            </Space>
                        </div>

                        {products.length > 0 && (
                            <Space.Compact style={{ marginBottom: 12, width: '100%', maxWidth: 360 }}>
                                <Select
                                    value={productFilterType}
                                    onChange={setProductFilterType}
                                    style={{ width: 100 }}
                                    options={[{ value: 'productName', label: '产品名称' }, { value: 'productId', label: '产品ID' }]}
                                />
                                <Input
                                    placeholder="产品名称/ID"
                                    allowClear
                                    prefix={<SearchOutlined />}
                                    style={{ width: '100%' }}
                                    value={productKeyword}
                                    onChange={(e) => setProductKeyword(e.target.value)}
                                />
                            </Space.Compact>
                        )}

                        <Table
                            size="small"
                            rowKey="pid"
                            dataSource={filteredSortedProducts}
                            rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                            pagination={filteredSortedProducts.length > 8 ? { pageSize: 8, showSizeChanger: false, showTotal: (t) => `共 ${t} 个产品` } : false}
                            locale={{ emptyText: products.length === 0 ? '请点击「添加产品」选择要绑定该策略的产品' : '未查询到相关产品' }}
                            columns={[
                                { title: '产品名称', dataIndex: 'pname', render: (t) => <Text strong>{t}</Text> },
                                { title: '产品ID', dataIndex: 'pid', width: 150, render: (t) => <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>{t}</Text> },
                                {
                                    title: '所属企业', dataIndex: 'entName',
                                    render: (_, r) => (
                                        <div>
                                            <div>{r.entName}</div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{r.entId}</Text>
                                        </div>
                                    ),
                                },
                                { title: '操作', key: 'action', width: 70, render: (_, r) => <Button type="link" danger size="small" onClick={() => handleRemoveProduct(r.pid)}>移除</Button> },
                            ]}
                        />
                    </div>
                </Form>
            </Drawer>

            <AddProductModal
                open={addProductOpen}
                existingPids={products.map((p) => p.pid)}
                onCancel={() => setAddProductOpen(false)}
                onSubmit={handleAddProduct}
            />
        </>
    );
};

// ══════════════════════════════════════════════
//  主组件：增值转化运营策略
// ══════════════════════════════════════════════
const TrialConvert = () => {
    const [strategies, setStrategies] = useState(MOCK_STRATEGIES);
    const [strategySearchType, setStrategySearchType] = useState('name');
    const [strategyKeyword, setStrategyKeyword] = useState('');
    const [appSearchType, setAppSearchType] = useState('appName');
    const [appKeyword, setAppKeyword] = useState('');
    const [productSearchType, setProductSearchType] = useState('productName');
    const [productKeyword, setProductKeyword] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingStrategy, setEditingStrategy] = useState(null);

    const handleAdd = () => { setEditingStrategy(null); setDrawerOpen(true); };
    const handleEdit = (record) => { setEditingStrategy(record); setDrawerOpen(true); };

    const handleSubmit = (values) => {
        if (editingStrategy) {
            setStrategies((prev) => prev.map((s) => (s.key === editingStrategy.key ? { ...s, ...values } : s)));
            message.success('编辑成功');
        } else {
            const newStrategy = {
                key: Date.now().toString(),
                strategyId: 'test-tc-' + String(Date.now()).slice(-5),
                ...values,
            };
            setStrategies((prev) => [...prev, newStrategy]);
            message.success('策略创建成功');
        }
        setDrawerOpen(false);
    };

    const handleToggle = (record) => {
        const willEnable = !record.enabled;
        Modal.confirm({
            title: willEnable ? '确定启用该策略吗？' : '确定禁用该策略吗？',
            icon: <ExclamationCircleFilled />,
            content: willEnable
                ? '启用后将在 App 端立即生效，命中「产品 × App」的设备开始适用试用转化玩法。'
                : '禁用后将在 App 端立即停止生效，已赠送存储与已解锁视频不受影响。',
            okText: willEnable ? '确定启用' : '确定禁用',
            okType: willEnable ? 'primary' : 'danger',
            cancelText: '取消',
            onOk: () => {
                setStrategies((prev) => prev.map((s) => (s.key === record.key ? { ...s, enabled: !s.enabled } : s)));
                message.success(willEnable ? '已启用' : '已禁用');
            },
        });
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '确定删除该策略吗？',
            icon: <ExclamationCircleFilled />,
            content: '删除后不影响已赠送存储与已解锁订单，历史数据保留。',
            okText: '确定删除', okType: 'danger', cancelText: '取消',
            onOk: () => { setStrategies((prev) => prev.filter((s) => s.key !== record.key)); message.success('已删除'); },
        });
    };

    // 已被其他策略占用的「系统平台-区域」key 集合（编辑时排除自身）
    const buildOccupiedPlatformKeys = (excludeKey) => {
        const keys = [];
        strategies.forEach((s) => {
            if (excludeKey && s.key === excludeKey) return;
            (s.appKeys || []).forEach((k) => {
                if (k.includes('__')) keys.push(k);
            });
        });
        return keys;
    };

    const filtered = strategies.filter((s) => {
        const skw = strategyKeyword.trim().toLowerCase();
        const hitStrategy = !skw || (strategySearchType === 'name' ? s.name.toLowerCase().includes(skw) : s.strategyId.toLowerCase().includes(skw));

        const akw = appKeyword.trim().toLowerCase();
        const appKeys = (s.appKeys || []).filter((k) => !k.includes('__'));
        const hitApp = !akw || (appSearchType === 'appName'
            ? appKeys.some((k) => getAppTitle(k).toLowerCase().includes(akw))
            : appKeys.some((k) => k.toLowerCase().includes(akw)));

        const pkw = productKeyword.trim().toLowerCase();
        const hitProduct = !pkw || (s.products || []).some((p) => productSearchType === 'productName'
            ? p.pname.toLowerCase().includes(pkw)
            : p.pid.toLowerCase().includes(pkw));

        const hitStatus = statusFilter === 'all' || (statusFilter === 'enabled' ? s.enabled : !s.enabled);
        return hitStrategy && hitApp && hitProduct && hitStatus;
    });

    const columns = [
        {
            title: '策略名称', key: 'name',
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Text strong>{r.name}</Text>
                    </div>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{r.strategyId}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: '转化服务类型', dataIndex: 'serviceType', width: 130,
            render: (t) => {
                const st = SERVICE_TYPE_MAP[t];
                return st ? <Tag color={st.color} style={{ border: 'none' }}>{st.label}</Tag> : <span style={{ color: '#ccc' }}>-</span>;
            },
        },
        { title: '免费回看', dataIndex: 'freePlaySeconds', width: 100, render: (t) => <Text>{t} 秒</Text> },
        {
            title: '单次解锁定价', key: 'price', width: 160,
            render: (_, r) => (
                <div>
                    <Text strong style={{ fontSize: 13 }}>¥ {r.unlockPriceCNY.toFixed(2)}</Text>
                    <Text strong style={{ fontSize: 13, marginLeft: 8 }}>$ {r.unlockPriceUSD.toFixed(2)}</Text>
                </div>
            ),
        },
        {
            title: '适用App', dataIndex: 'appKeys', width: 130,
            render: (appKeys) => {
                const keys = appKeys || [];
                const appCount = keys.filter((k) => !k.includes('__')).length;
                const platformCount = keys.filter((k) => k.includes('__')).length;
                if (appCount === 0) return <span style={{ color: '#ccc' }}>-</span>;
                const treeText = buildAppTreeText(keys);
                return (
                    <Tooltip title={<pre style={{ margin: 0, fontSize: 12 }}>{treeText}</pre>}>
                        <div>
                            <Text>{appCount}个App应用</Text>
                            <div><Text type="secondary" style={{ fontSize: 12 }}>{platformCount}个平台</Text></div>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: '绑定产品', dataIndex: 'products', width: 120,
            render: (products) => {
                const list = products || [];
                if (list.length === 0) return <span style={{ color: '#ccc' }}>-</span>;
                const tooltip = list.map((p) => `${p.pname}（${p.pid}）`).join('\n');
                return (
                    <Tooltip title={<pre style={{ margin: 0, fontSize: 12 }}>{tooltip}</pre>}>
                        <Tag color="blue" style={{ border: 'none', cursor: 'pointer' }}>{list.length} 个产品</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: '状态', dataIndex: 'enabled', width: 90,
            render: (enabled) => enabled
                ? <Tag color="green" style={{ border: 'none' }}>启用</Tag>
                : <Tag color="default" style={{ border: 'none' }}>禁用</Tag>,
        },
        {
            title: '操作', key: 'action', width: 200,
            render: (_, record) => (
                <Space size={0}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                    <Button type="link" size="small" onClick={() => handleToggle(record)}>{record.enabled ? '禁用' : '启用'}</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>删除</Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Breadcrumb style={{ marginBottom: 12 }} items={[{ title: '运营管理' }, { title: '增值转化运营' }]} />
            <Title level={4} style={{ marginBottom: 16 }}>增值转化运营</Title>

            <Alert
                type="info" showIcon closable style={{ marginBottom: 16, borderRadius: 8 }}
                message="平台级运营营收手段（不对企业开放）。绑定「产品 × App」后，命中设备的 App 端可免费查看事件云回看 N 秒，超出后引导用户付费解锁单个视频；仅平台运营可操作。"
            />

            <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Form layout="inline" style={{ rowGap: 8 }}>
                    <Form.Item style={{ marginBottom: 0, marginRight: 8, flex: '1 1 280px', minWidth: 280 }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Select
                                value={strategySearchType}
                                onChange={setStrategySearchType}
                                style={{ width: 110 }}
                                options={[{ value: 'name', label: '策略名称' }, { value: 'strategyId', label: '策略ID' }]}
                            />
                            <Input
                                placeholder="策略名称/ID"
                                allowClear
                                style={{ width: '100%' }}
                                value={strategyKeyword}
                                onChange={(e) => setStrategyKeyword(e.target.value)}
                            />
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, marginRight: 8, flex: '1 1 280px', minWidth: 280 }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Select
                                value={appSearchType}
                                onChange={setAppSearchType}
                                style={{ width: 110 }}
                                options={[{ value: 'appName', label: '应用名称' }, { value: 'appId', label: 'AppID' }]}
                            />
                            <Input
                                placeholder="应用名称/AppID"
                                allowClear
                                style={{ width: '100%' }}
                                value={appKeyword}
                                onChange={(e) => setAppKeyword(e.target.value)}
                            />
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, marginRight: 8, flex: '1 1 280px', minWidth: 280 }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Select
                                value={productSearchType}
                                onChange={setProductSearchType}
                                style={{ width: 110 }}
                                options={[{ value: 'productName', label: '产品名称' }, { value: 'productId', label: '产品ID' }]}
                            />
                            <Input
                                placeholder="产品名称/ID"
                                allowClear
                                style={{ width: '100%' }}
                                value={productKeyword}
                                onChange={(e) => setProductKeyword(e.target.value)}
                            />
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, marginRight: 8 }}>
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: 140 }}
                            options={[{ value: 'all', label: '全部状态' }, { value: 'enabled', label: '启用' }, { value: 'disabled', label: '禁用' }]}
                        />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />}>查询</Button>
                            <Button icon={<ReloadOutlined />} onClick={() => {
                                setStrategyKeyword(''); setStrategySearchType('name');
                                setAppKeyword(''); setAppSearchType('appName');
                                setProductKeyword(''); setProductSearchType('productName');
                                setStatusFilter('all');
                            }}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">共 {filtered.length} 条记录</Text>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加策略</Button>
                </div>
                <Table
                    columns={columns}
                    dataSource={filtered}
                    rowKey="key"
                    bordered={false}
                    pagination={filtered.length > 10 ? { showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` } : false}
                    locale={{ emptyText: '未查询到相关数据' }}
                />
            </Card>

            <StrategyFormDrawer
                open={drawerOpen}
                editingRecord={editingStrategy}
                occupiedPlatformKeys={buildOccupiedPlatformKeys(editingStrategy?.key)}
                onCancel={() => setDrawerOpen(false)}
                onSubmit={handleSubmit}
            />
        </div>
    );
};

export default TrialConvert;
